import React, { useRef, useState } from 'react';
import { Prospect } from 'crono-fe-common/types/prospect';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import {
  Input,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  EditMdIcon,
} from '@crono-one/design-system';
import SequenceHandlingButton from 'pages/accountDrawer/contactView/prospectCard/sequenceHandlingButton';
import { useTaskTypeOptions } from 'pages/taskBoard/upsertModal/useTaskTypeOptions';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';

interface TaskSubjectDisplayProps {
  task?: TaskTodo | null;
  prospect?: Prospect | null;
  editable?: boolean;
  onEdit?: (task: TaskTodo) => void;
  taskType?: TaskTodoType;
}

export const TaskSubjectDisplay = ({
  task,
  prospect,
  editable,
  onEdit,
  taskType,
}: TaskSubjectDisplayProps) => {
  const { getTaskTypeOptionConfig } = useTaskTypeOptions();
  const taskTypeConfig = getTaskTypeOptionConfig(
    (task?.type ?? taskType)!,
    task?.subtype,
  );

  const defaultTitle =
    `New ${taskTypeConfig?.title.toLowerCase()} for ${prospect?.name ?? ''}`.trim();
  const TaskIcon = taskTypeConfig?.IconSm;

  const [isEditing, setIsEditing] = useState(false);
  const [titleValue, setTitleValue] = useState(task?.subject || defaultTitle);
  const [inputWidth, setInputWidth] = useState<number | undefined>(undefined);
  const spanRef = useRef<HTMLSpanElement>(null);

  function handleSave() {
    setIsEditing(false);
    if (!onEdit) return;
    const newSubject = titleValue || defaultTitle;
    if (task) {
      onEdit({ ...task, subject: newSubject });
    } else {
      onEdit({ subject: newSubject } as TaskTodo);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setTitleValue(task?.subject || defaultTitle);
      setIsEditing(false);
    }
  }

  // ── Editable title (used for call tasks) ──────────────────────────────────

  let editableTitle: React.ReactNode = null;
  if (editable) {
    if (isEditing) {
      editableTitle = (
        <div className="flex items-center gap-2 ml-auto justify-end overflow-hidden flex-1 px-2">
          {TaskIcon && <TaskIcon className="size-4" />}

          <Input
            variant="text"
            value={titleValue}
            placeholder="Type task name"
            autoFocus
            className="text-sm"
            style={
              inputWidth !== undefined ? { width: inputWidth + 2 } : undefined
            }
            onChange={(e) => setTitleValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
          />

          <EditMdIcon className="size-6  invisible text-neutral-500" />
        </div>
      );
    } else {
      editableTitle = (
        <div className="flex items-center ml-auto justify-end overflow-hidden w-full">
          <div
            onClick={() => {
              setInputWidth(spanRef.current?.offsetWidth);
              setIsEditing(true);
            }}
            className="flex items-center gap-2 hover:bg-neutral-100 rounded-lg p-2 cursor-pointer group overflow-hidden flex-1"
          >
            {TaskIcon && <TaskIcon className="size-4" />}

            <span ref={spanRef} className="text-sm flex-1 truncate">
              {titleValue || defaultTitle}
            </span>

            <EditMdIcon className="size-6 group-hover:visible invisible text-neutral-500" />
          </div>
        </div>
      );
    }
  }

  const sequenceButton =
    task?.sequenceInstance && prospect ? (
      <div className="flex justify-end ml-auto">
        <SequenceHandlingButton prospect={prospect} />
      </div>
    ) : null;

  // ── Non-editable ──────────────────────────────────────────────────────────

  if (!editable) {
    if (!task?.subject && !sequenceButton) return null;

    return (
      <div className="flex flex-col gap-2 justify-end overflow-hidden flex-1 max-w-fit">
        {sequenceButton}
        {task?.subject && (
          <div className="flex items-center gap-2 text-sm font-medium max-w-72 overflow-hidden">
            {TaskIcon && <TaskIcon className="size-4" />}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="truncate flex-1">{task.subject}</span>
              </TooltipTrigger>
              <TooltipContent className="z-5000">{task.subject}</TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    );
  }

  // ── Editable (card / manual task) ─────────────────────────────────────────

  return (
    <div className="flex flex-col gap-2 justify-end overflow-hidden flex-1 max-w-fit">
      {sequenceButton}
      {editableTitle}
    </div>
  );
};
